import { PlusOutlined, ReloadOutlined, RightOutlined, WarningOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { listCustomers } from '../api/customers';
import type { CustomerListItem } from '../api/customers';
import { ApiError } from '../api/errors';
import { linkOpportunityProject } from '../api/opportunities';
import { ProjectPriority, createProject, listProjectTypes, listProjects } from '../api/projects';
import type {
  ProjectHealthValue,
  ProjectListItem,
  ProjectPriorityValue,
  ProjectStatusValue,
  ProjectType,
} from '../api/projects';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { AppRoutes } from '../routes';
import { BrandColors } from '../theme/brand';
import {
  formatDate,
  healthOptions,
  healthTag,
  priorityOptions,
  priorityTag,
  progressBar,
  statusOptions,
  statusTag,
} from './projects/projectDisplay';

/** Giá trị của biểu mẫu mở dự án mới. */
interface ProjectFormValues {
  readonly code: string;
  readonly name: string;
  readonly nameEn: string | null;
  readonly projectTypeId: string;
  readonly customerId: string;
  readonly priority: ProjectPriorityValue;
  readonly isInternal: boolean;
  readonly description: string | null;
}

/** Dữ liệu của một trang đã nạp xong, kèm khóa nhận dạng lần nạp đó. */
interface LoadedPage {
  readonly key: string;
  readonly items: readonly ProjectListItem[];
  readonly totalCount: number;
}

/** Số bản ghi mỗi trang. Khớp với mặc định của máy chủ. */
const pageSize = 25;

/**
 * Tên các tham số địa chỉ dùng khi mở dự án từ một cơ hội bán hàng.
 *
 * <b>Vì sao đi qua địa chỉ chứ không dựng một biểu mẫu riêng ở trang cơ hội.</b> Mở dự án có bảy ô
 * và vài quy tắc riêng; dựng bản thứ hai nghĩa là hai biểu mẫu giống nhau ở hai chỗ, và chúng sẽ
 * lệch nhau ở lần sửa sau. Đi qua địa chỉ thì chỉ có một biểu mẫu duy nhất, và nó là biểu mẫu đã
 * được dùng hằng ngày.
 */
const openFromOpportunityParameters = {
  /** Định danh cơ hội cần gắn dự án vào sau khi mở xong. */
  opportunityId: 'co-hoi',

  /** Khách hàng của cơ hội, điền sẵn vào biểu mẫu. */
  customerId: 'khach-hang',

  /** Tên cơ hội, điền sẵn làm tên dự án để người dùng sửa lại cho gọn. */
  name: 'ten',
} as const;

/**
 * Số bản ghi nạp cho các ô chọn.
 *
 * Bằng giới hạn lớn nhất của máy chủ. Danh mục loại dự án và cây khách hàng của một công ty tích hợp
 * hệ thống thị giác có hàng chục mục, không phải hàng nghìn, nên một lần nạp là đủ.
 */
const lookupPageSize = 200;

/**
 * Lấy danh mục cần cho biểu mẫu mở dự án.
 *
 * Tách khỏi thành phần và <b>không đặt trạng thái nào</b>: nhờ vậy cả nơi mở biểu mẫu bằng tay lẫn
 * hiệu ứng mở biểu mẫu từ cơ hội bán hàng đều dùng chung một nguồn, mà không chỗ nào phải chép lại
 * hai lời gọi này.
 */
async function fetchLookups(): Promise<{
  readonly projectTypes: readonly ProjectType[];
  readonly customers: readonly CustomerListItem[];
}> {
  const [types, customerPage] = await Promise.all([
    listProjectTypes({ onlyActive: true, pageSize: lookupPageSize }),
    listCustomers({ onlyOpenForBusiness: true, pageSize: lookupPageSize }),
  ]);

  return { projectTypes: types.items, customers: customerPage.items };
}

/**
 * Trang danh sách dự án.
 *
 * <b>Mặc định chỉ hiện dự án chưa đóng.</b> Bảng theo dõi hằng ngày gần như không bao giờ muốn thấy
 * dự án đã đóng hoặc đã hủy, và bắt người dùng bỏ chọn chúng mỗi lần mở màn hình là một thao tác lặp
 * lại mà máy làm được. Vẫn có ô để xem tất cả khi cần tra cứu.
 *
 * <b>Sắp theo mức ưu tiên rồi tới mốc cam kết</b> — do máy chủ sắp. Người mở màn hình này đang hỏi
 * <i>hôm nay lo việc gì trước</i>, và câu trả lời là dự án ưu tiên cao sắp tới hạn, không phải dự án
 * mới mở nhất.
 *
 * <b>Không sửa tại chỗ trên bảng.</b> Mỗi nhóm thông tin của dự án có một quyền riêng — sửa mốc cam
 * kết và đổi người quản lý là hai quyền nhạy cảm — nên việc sửa nằm ở trang chi tiết, nơi từng nhóm
 * hiện ra hoặc bị ẩn theo quyền thật.
 */
export function ProjectPage(): ReactNode {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParameters, setSearchParameters] = useSearchParams();

  /*
    Cơ hội đang chờ được gắn dự án — SUY RA từ địa chỉ, không sao chép sang một trạng thái riêng.

    Sao chép sang trạng thái thì phải đặt nó trong một hiệu ứng, mà đặt trạng thái trong hiệu ứng
    gây ra một vòng vẽ lại dây chuyền. Suy ra thì địa chỉ là nguồn duy nhất: người dùng tải lại
    trang giữa chừng vẫn thấy đúng biểu mẫu đang mở, vì lúc đó dự án chưa được tạo.
  */
  const opportunityToLink = searchParameters.get(openFromOpportunityParameters.opportunityId);

  const canCreate = hasPermission(PermissionCodes.ProjectCreate);

  const [form] = Form.useForm<ProjectFormValues>();

  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatusValue | null>(null);
  const [healthFilter, setHealthFilter] = useState<ProjectHealthValue | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<ProjectPriorityValue | null>(null);
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [loaded, setLoaded] = useState<LoadedPage | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isManualFormOpen, setIsManualFormOpen] = useState(false);

  // Biểu mẫu mở khi người dùng tự bấm, HOẶC khi tới từ trang cơ hội bán hàng.
  const isFormOpen = isManualFormOpen || opportunityToLink !== null;
  const [isSaving, setIsSaving] = useState(false);

  const [projectTypes, setProjectTypes] = useState<readonly ProjectType[]>([]);
  const [customers, setCustomers] = useState<readonly CustomerListItem[]>([]);

  const requestKey = useMemo(
    () =>
      JSON.stringify({
        searchTerm,
        statusFilter,
        healthFilter,
        priorityFilter,
        onlyOpen,
        overdueOnly,
        pageNumber,
        reloadToken,
      }),
    [
      searchTerm,
      statusFilter,
      healthFilter,
      priorityFilter,
      onlyOpen,
      overdueOnly,
      pageNumber,
      reloadToken,
    ],
  );

  const isLoading = loaded?.key !== requestKey && errorMessage === null;

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      setErrorMessage(null);

      try {
        /*
          BỎ HẲN khóa khi không lọc, thay vì gán undefined cho nó.

          Hai lý do. Về nghiệp vụ: máy chủ phân biệt "không lọc" với "lọc theo giá trị này", nên một
          khóa gửi lên với giá trị rỗng vẫn là một điều kiện lọc thật. Về kiểu dữ liệu: dự án bật
          exactOptionalPropertyTypes, nên gán undefined cho một thuộc tính tùy chọn là lỗi
          biên dịch — cấu hình đó tồn tại đúng để bắt loại nhầm lẫn này.
        */
        const page = await listProjects({
          searchTerm,
          onlyOpen,
          overdueOnly,
          pageNumber,
          pageSize,
          ...(statusFilter === null ? {} : { status: statusFilter }),
          ...(healthFilter === null ? {} : { health: healthFilter }),
          ...(priorityFilter === null ? {} : { priority: priorityFilter }),
        });

        if (!isCurrent) {
          return;
        }

        setLoaded({ key: requestKey, items: page.items, totalCount: page.totalCount });
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được danh sách dự án.',
        );
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [
    requestKey,
    searchTerm,
    statusFilter,
    healthFilter,
    priorityFilter,
    onlyOpen,
    overdueOnly,
    pageNumber,
  ]);

  const reload = useCallback(() => { setReloadToken((token) => token + 1); }, []);

  /**
   * Nạp dữ liệu cho hai ô chọn của biểu mẫu.
   *
   * Chỉ nạp khi mở biểu mẫu, không nạp lúc mở trang: phần lớn lần mở trang là để xem danh sách chứ
   * không phải để mở dự án mới, và hai lời gọi thừa ở mỗi lần mở trang là chi phí không cần thiết.
   */
  async function loadLookups(): Promise<void> {
    try {
      const lookups = await fetchLookups();

      setProjectTypes(lookups.projectTypes);
      setCustomers(lookups.customers);
    } catch (error) {
      message.error(
        error instanceof ApiError ? error.message : 'Không tải được danh mục cho biểu mẫu.',
      );
    }
  }

  /**
   * Đóng biểu mẫu mở dự án.
   *
   * Dọn cả tham số trên địa chỉ, vì trạng thái mở của biểu mẫu được suy ra từ đó. Chỉ tắt cờ mở
   * bằng tay thôi thì biểu mẫu vẫn mở lại ngay khi vẽ lần sau.
   */
  function closeForm(): void {
    setIsManualFormOpen(false);

    if (searchParameters.get(openFromOpportunityParameters.opportunityId) !== null) {
      setSearchParameters({}, { replace: true });
    }
  }

  function openCreate(): void {
    form.resetFields();
    form.setFieldsValue({ priority: ProjectPriority.Normal, isInternal: false });
    setIsManualFormOpen(true);
    void loadLookups();
  }

  /*
    Tới từ trang cơ hội bán hàng: mở sẵn biểu mẫu với khách hàng và tên đã điền.

    Xóa tham số khỏi địa chỉ NGAY sau khi đọc. Không xóa thì người dùng bấm nút Quay lại của trình
    duyệt hoặc tải lại trang sẽ mở lại biểu mẫu một lần nữa, và họ dễ mở nhầm hai dự án cho cùng
    một thương vụ.
  */
  useEffect(() => {
    if (opportunityToLink === null) {
      return;
    }

    const customerId = searchParameters.get(openFromOpportunityParameters.customerId);
    const name = searchParameters.get(openFromOpportunityParameters.name);

    /*
      Chỉ điền sẵn biểu mẫu và nạp danh mục — KHÔNG đặt trạng thái React nào ở đây. Việc biểu mẫu
      có mở hay không đã được suy ra từ địa chỉ phía trên.
    */
    form.setFieldsValue({
      priority: ProjectPriority.Normal,
      isInternal: false,
      ...(customerId === null ? {} : { customerId }),
      ...(name === null ? {} : { name }),
    });

    let isCurrent = true;

    async function loadLookupsForForm(): Promise<void> {
      try {
        const lookups = await fetchLookups();

        if (!isCurrent) {
          return;
        }

        setProjectTypes(lookups.projectTypes);
        setCustomers(lookups.customers);
      } catch (error) {
        if (isCurrent) {
          message.error(
            error instanceof ApiError ? error.message : 'Không tải được danh mục cho biểu mẫu.',
          );
        }
      }
    }

    void loadLookupsForForm();

    return () => {
      isCurrent = false;
    };
  }, [opportunityToLink, searchParameters, form]);

  async function submit(values: ProjectFormValues): Promise<void> {
    setIsSaving(true);

    try {
      const created = await createProject({
        code: values.code,
        name: values.name,
        nameEn: values.nameEn ?? null,
        projectTypeId: values.projectTypeId,
        customerId: values.customerId,

        // Rỗng nghĩa là chính người đang mở dự án — trường hợp thường gặp nhất ở công ty nhỏ. Người
        // quản lý khác được chỉ định ở trang chi tiết, nơi có quyền riêng cho việc đó.
        projectManagerUserId: null,
        priority: values.priority,
        isInternal: values.isInternal,
        description: values.description ?? null,
      });

      message.success(`Đã mở dự án ${created.code}.`);

      if (opportunityToLink !== null) {
        /*
          Gắn dự án vào cơ hội trong một lời gọi RIÊNG, và lỗi ở bước này KHÔNG được làm hỏng việc
          vừa xong. Dự án đã mở thật rồi; báo cho người dùng biết phần còn thiếu, thay vì hiện một
          thông báo lỗi khiến họ tưởng dự án chưa được mở và bấm lại lần nữa.
        */
        try {
          await linkOpportunityProject(opportunityToLink, created.id);
          message.success('Đã gắn dự án này vào cơ hội bán hàng.');
        } catch (error) {
          message.warning(
            'Dự án đã mở xong nhưng chưa gắn được vào cơ hội: '
            + (error instanceof ApiError ? error.message : 'lỗi không rõ.'),
          );
        }

      }

      closeForm();
      form.resetFields();
      void navigate(`${AppRoutes.Project}/${created.id}`);
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không mở được dự án.');
    } finally {
      setIsSaving(false);
    }
  }

  const columns: TableColumnsType<ProjectListItem> = [
    {
      title: 'Mã dự án',
      dataIndex: 'code',
      width: 180,
      render: (code: string) => <Typography.Text strong>{code}</Typography.Text>,
    },
    {
      title: 'Tên dự án',
      dataIndex: 'name',
      render: (name: string, project) => (
        <Space direction="vertical" size={0}>
          <span>{name}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {project.projectTypeName}
            {project.isInternal ? ' · Nội bộ' : ''}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 140,
      render: (status: ProjectStatusValue) => statusTag(status),
    },
    {
      title: 'Ưu tiên',
      dataIndex: 'priority',
      width: 110,
      render: (priority: ProjectPriorityValue) => priorityTag(priority),
    },
    {
      title: 'Tình trạng',
      dataIndex: 'health',
      width: 120,
      render: (health: ProjectHealthValue) => healthTag(health, null),
    },
    {
      title: 'Tiến độ',
      key: 'progress',
      width: 160,
      render: (_, project) =>
        progressBar(project.plannedProgressPercent, project.actualProgressPercent),
    },
    {
      title: 'Hạn cam kết',
      key: 'committedCompletionDate',
      width: 150,
      render: (_, project) => (
        <Space size={4}>
          <span>{formatDate(project.committedCompletionDate)}</span>
          {project.isOverdue ? (
            <Tooltip title="Đã quá mốc cam kết mà chưa kết thúc thực tế.">
              <WarningOutlined style={{ color: BrandColors.red }} />
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, project) => (
        <Button
          size="small"
          type="text"
          icon={<RightOutlined />}
          onClick={() => { void navigate(`${AppRoutes.Project}/${project.id}`); }}
        />
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Dự án
        </Typography.Title>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Tìm theo mã hoặc tên"
            style={{ width: 240 }}
            onSearch={(value) => {
              setSearchTerm(value);
              setPageNumber(1);
            }}
          />
          <Select<ProjectStatusValue>
            allowClear
            placeholder="Trạng thái"
            style={{ width: 160 }}
            options={statusOptions}
            onChange={(value: ProjectStatusValue | undefined) => {
              // Khai rõ kiểu có thể không xác định: khi người dùng bấm nút xóa lựa chọn, thư viện
              // giao diện gọi lại với undefined chứ không phải một giá trị hợp lệ.
              setStatusFilter(value ?? null);
              setPageNumber(1);
            }}
          />
          <Select<ProjectHealthValue>
            allowClear
            placeholder="Tình trạng"
            style={{ width: 150 }}
            options={healthOptions}
            onChange={(value: ProjectHealthValue | undefined) => {
              // Khai rõ kiểu có thể không xác định: khi người dùng bấm nút xóa lựa chọn, thư viện
              // giao diện gọi lại với undefined chứ không phải một giá trị hợp lệ.
              setHealthFilter(value ?? null);
              setPageNumber(1);
            }}
          />
          <Select<ProjectPriorityValue>
            allowClear
            placeholder="Ưu tiên"
            style={{ width: 140 }}
            options={priorityOptions}
            onChange={(value: ProjectPriorityValue | undefined) => {
              // Khai rõ kiểu có thể không xác định: khi người dùng bấm nút xóa lựa chọn, thư viện
              // giao diện gọi lại với undefined chứ không phải một giá trị hợp lệ.
              setPriorityFilter(value ?? null);
              setPageNumber(1);
            }}
          />
          <Checkbox
            checked={onlyOpen}
            onChange={(event) => {
              setOnlyOpen(event.target.checked);
              setPageNumber(1);
            }}
          >
            Chưa đóng
          </Checkbox>
          <Checkbox
            checked={overdueOnly}
            onChange={(event) => {
              setOverdueOnly(event.target.checked);
              setPageNumber(1);
            }}
          >
            Quá hạn
          </Checkbox>
          <Button icon={<ReloadOutlined />} onClick={reload}>
            Tải lại
          </Button>
          <Button type="primary" icon={<PlusOutlined />} disabled={!canCreate} onClick={openCreate}>
            Mở dự án
          </Button>
        </Space>
      </Space>

      {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

      <Table<ProjectListItem>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={loaded?.items}
        loading={isLoading}
        onRow={(project) => ({
          onDoubleClick: () => { void navigate(`${AppRoutes.Project}/${project.id}`); },
        })}
        pagination={{
          current: pageNumber,
          pageSize,
          total: loaded?.totalCount ?? 0,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0].toString()}–${range[1].toString()} trong ${total.toString()}`,
          onChange: setPageNumber,
        }}
      />

      <Modal
        open={isFormOpen}
        title="Mở dự án mới"
        okText="Mở dự án"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={closeForm}
        onOk={() => { form.submit(); }}
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={(values) => void submit(values)}>
          <Form.Item
            name="code"
            label="Mã dự án"
            rules={[{ required: true, message: 'Chưa nhập mã dự án.' }]}
            extra="Nhập tay và không sửa được về sau: mã này đi vào hợp đồng và biên bản nghiệm thu."
          >
            <Input placeholder="AOI-SDV-2026-003" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Tên dự án"
            rules={[{ required: true, message: 'Chưa nhập tên dự án.' }]}
          >
            <Input placeholder="Hệ thống kiểm tra ngoại quan dây chuyền 3" />
          </Form.Item>
          <Form.Item name="nameEn" label="Tên tiếng Anh">
            <Input />
          </Form.Item>
          <Form.Item
            name="projectTypeId"
            label="Loại dự án"
            rules={[{ required: true, message: 'Chưa chọn loại dự án.' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Chọn loại dự án"
              options={projectTypes.map((projectType) => ({
                value: projectType.id,
                label: `${projectType.code} — ${projectType.name}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="customerId"
            label="Khách hàng"
            rules={[{ required: true, message: 'Chưa chọn khách hàng.' }]}
            extra="Chỉ hiện khách đang cho giao dịch. Dự án nội bộ thì chọn chính pháp nhân MVA Lab."
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Chọn khách hàng"
              options={customers.map((customer) => ({
                value: customer.id,
                label: `${customer.code} — ${customer.shortName}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="priority" label="Mức độ ưu tiên" initialValue={ProjectPriority.Normal}>
            <Select<ProjectPriorityValue> options={priorityOptions} />
          </Form.Item>
          <Form.Item
            name="isInternal"
            label="Dự án nội bộ hoặc nghiên cứu"
            valuePropName="checked"
            initialValue={false}
            extra="Dự án nội bộ chỉ có chi phí, không có doanh thu, nên được tách khỏi bảng lãi lỗ theo dự án."
          >
            <Switch />
          </Form.Item>
          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
